import qs from 'qs'
import { urlAPI, USER_ID_KEY } from '../../../urls'
import { getCommonHeaders } from '../../../functions/shared/api/client'

export type HandoverPerson = { id: number, name: string }
export type HandoverEvent = {
    id: string, operationId: string, type: string, recordedAt: string, occurredAt: string,
    receptionistId: number, receptionistName: string, note: string | null,
    custodyAfter: string, keyStatusAfter: string, viewingId: number,
    from?: HandoverPerson, to: HandoverPerson,
    sourceViewingId?: number, sourceKeyBookingId?: number,
    targetViewingId: number, targetKeyBookingId: number
}
export type KeyOperation = {
    id: string, kind: 'transfer' | 'received', createdAt: string,
    admin: HandoverPerson, recipient: HandoverPerson, unitId: number,
    source?: { viewingId: number, keyId: number, holder: HandoverPerson },
    pickup: string, returnAt: string, note: string,
    viewing: Record<string, unknown>, viewingId?: number, keyId?: number
}
type Entity = { id: number, attributes: Record<string, any> }
const storageKey = (): string => `bso:key-operations:v1:${urlAPI}:${localStorage.getItem(USER_ID_KEY)}`
const active = new Set<string>()
export const historyArray = (value: unknown): any[] => {
    if (value == null) return []
    const parsed = typeof value === 'string' ? JSON.parse(value) : value
    if (!Array.isArray(parsed)) throw new Error('Unable to read key history. Nothing was overwritten.')
    return parsed
}
export const dubaiInput = (date = new Date()): string => new Date(date.getTime() + 4 * 3600000).toISOString().slice(0, 16)
export const dubaiUTC = (value: string): string => {
    if (!/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}$/.test(value)) throw new Error('Choose a valid Dubai date and time.')
    const parsed = new Date(`${value}:00+04:00`)
    if (!Number.isFinite(parsed.getTime()) || dubaiInput(parsed) !== value) throw new Error('Choose a valid Dubai date and time.')
    return parsed.toISOString()
}
export function pendingKeyOperations(): KeyOperation[] {
    const raw = localStorage.getItem(storageKey())
    if (raw == null) return []
    const parsed = JSON.parse(raw)
    if (!Array.isArray(parsed)) throw new Error('Unable to read pending key operations.')
    return parsed
}
function save(operation: KeyOperation): void {
    const operations = pendingKeyOperations().filter((item) => item.id !== operation.id)
    localStorage.setItem(storageKey(), JSON.stringify([...operations, operation]))
    window.dispatchEvent(new Event('bso:key-operation-progress'))
}
export function finishKeyOperation(id: string): void {
    localStorage.setItem(storageKey(), JSON.stringify(pendingKeyOperations().filter((item) => item.id !== id)))
    window.dispatchEvent(new Event('bso:key-operation-progress'))
}
export function beginKeyOperation(input: Omit<KeyOperation, 'id' | 'createdAt' | 'viewingId' | 'keyId'>): KeyOperation {
    if (!Number.isSafeInteger(input.recipient.id) || input.recipient.id <= 0 || !Number.isSafeInteger(input.admin.id) || input.admin.id <= 0) throw new Error('Select an agent and sign in as an administrator.')
    if (input.source?.holder.id === input.recipient.id) throw new Error('Select a different agent.')
    if (!Number.isFinite(Date.parse(input.pickup)) || !Number.isFinite(Date.parse(input.returnAt)) || Date.parse(input.returnAt) <= Date.parse(input.pickup)) throw new Error('Return time must be after receipt time.')
    const duplicate = pendingKeyOperations().find((item) => input.source ? item.source?.keyId === input.source.keyId : item.kind === 'received' && item.unitId === input.unitId)
    if (duplicate != null) throw new Error('An unfinished key operation exists. Continue it from the key archive.')
    const operation = { ...input, id: crypto.randomUUID(), createdAt: new Date().toISOString() }
    save(operation) // Persist before the first network mutation.
    return operation
}
export async function keyRequest(method: 'GET' | 'POST' | 'PUT', collection: string, options: { id?: number, body?: Record<string, unknown>, query?: string } = {}): Promise<any> {
    const controller = new AbortController()
    const timer = window.setTimeout(() => controller.abort(), 30000)
    try {
        const response = await fetch(urlAPI, { method: 'POST', headers: getCommonHeaders(), signal: controller.signal, body: JSON.stringify({ method, collection, ...options }) })
        const json = await response.json()
        if (!response.ok || json.success !== true || json.response?.error != null || json.response?.data == null) {
            const error = json.response?.error?.message ?? json.error?.message ?? json.error
            throw new Error(typeof error === 'string' ? error : 'Unable to save the key operation.')
        }
        return json.response.data
    } finally { window.clearTimeout(timer) }
}
const readViewing = async (id: number): Promise<Entity> => await keyRequest('GET', 'viewings', { id, query: qs.stringify({ populate: ['Unit', 'User', 'KeyBooking'] }) })
const readKey = async (id: number): Promise<Entity> => await keyRequest('GET', 'unit-keys', { id, query: qs.stringify({ populate: ['Unit', 'Holder', 'Viewings'] }) })
const relationID = (relation: any): number => Number(relation?.data?.id)
const marker = (operation: KeyOperation): string => `[BSO key operation ${operation.id}]`
function eventFor(operation: KeyOperation, outgoing = false): HandoverEvent {
    return {
        id: `${operation.id}:${outgoing ? 'out' : 'in'}`, operationId: operation.id,
        type: outgoing ? 'key_transferred' : operation.source ? 'key_received_transfer' : 'key_received_manual',
        recordedAt: operation.createdAt, occurredAt: operation.pickup,
        receptionistId: operation.admin.id, receptionistName: operation.admin.name, note: operation.note || null,
        custodyAfter: outgoing ? 'transferred' : 'with_broker', keyStatusAfter: outgoing ? 'Completed' : 'In-process',
        viewingId: outgoing ? operation.source!.viewingId : operation.viewingId!,
        from: operation.source?.holder, to: operation.recipient,
        sourceViewingId: operation.source?.viewingId, sourceKeyBookingId: operation.source?.keyId,
        targetViewingId: operation.viewingId!, targetKeyBookingId: operation.keyId!
    }
}
function verifyTarget(operation: KeyOperation, viewing: Entity, key: Entity): void {
    if (relationID(viewing.attributes.Unit) !== operation.unitId || relationID(viewing.attributes.User) !== operation.recipient.id ||
        relationID(key.attributes.Unit) !== operation.unitId || relationID(key.attributes.Holder) !== operation.recipient.id ||
        relationID(viewing.attributes.KeyBooking) !== key.id || key.attributes.Status !== 'In-process') throw new Error('The receiving booking changed. Review the records before continuing.')
}
async function execute(operation: KeyOperation): Promise<void> {
    let sourceKey: Entity | undefined
    if (operation.source != null) {
        const sourceViewing = await readViewing(operation.source.viewingId)
        sourceKey = await readKey(operation.source.keyId)
        const closedByUs = historyArray(sourceKey.attributes.ReceptionHistory).some((event) => event.id === `${operation.id}:out`)
        if (relationID(sourceViewing.attributes.KeyBooking) !== operation.source.keyId || relationID(sourceKey.attributes.Unit) !== operation.unitId ||
            relationID(sourceKey.attributes.Holder) !== operation.source.holder.id || (!closedByUs && sourceKey.attributes.Status !== 'In-process')) throw new Error('The original key holder or status changed. Review the record.')
    }
    // Always reconcile before POST, including when a prior POST response was lost.
    if (operation.viewingId == null) {
        const found: Entity[] = await keyRequest('GET', 'viewings', { query: qs.stringify({ filters: { Complaint: { $contains: marker(operation) }, Unit: { id: operation.unitId }, User: { id: operation.recipient.id } } }) })
        if (!Array.isArray(found)) throw new Error('Unable to reconcile viewing records. No create request was sent.')
        if (found.length > 1) throw new Error('Multiple viewing records match this operation. Review them before continuing.')
        if (found.length === 1) operation.viewingId = found[0].id
        else {
            const created = await keyRequest('POST', 'viewings', { body: { ...operation.viewing, Unit: operation.unitId, User: operation.recipient.id, Complaint: [operation.viewing.Complaint, marker(operation)].filter(Boolean).join('\n') } })
            operation.viewingId = created.id
        }
        save(operation)
    }
    if (operation.keyId == null) {
        const found: Entity[] = await keyRequest('GET', 'unit-keys', { query: qs.stringify({ filters: { Viewings: { id: operation.viewingId } } }) })
        if (!Array.isArray(found)) throw new Error('Unable to reconcile key records. No create request was sent.')
        if (found.length > 1) throw new Error('Multiple key bookings are linked to the receiving viewing.')
        if (found.length === 1) {
            if (!historyArray(found[0].attributes.ReceptionHistory).some((event) => event.operationId === operation.id)) throw new Error('Another key booking already exists for this viewing.')
            operation.keyId = found[0].id
        } else {
            const created = await keyRequest('POST', 'unit-keys', { body: {
                Unit: operation.unitId, Holder: operation.recipient.id, Viewings: [operation.viewingId],
                Status: 'In-process', PickUpDate: operation.pickup, DropOutDate: operation.returnAt,
                ReceptionHistory: [{ ...eventFor(operation), targetKeyBookingId: null }]
            } })
            operation.keyId = created.id
        }
        save(operation)
    }
    let targetKey = await readKey(operation.keyId!)
    verifyTarget(operation, await readViewing(operation.viewingId!), targetKey)
    const targetHistory = historyArray(targetKey.attributes.ReceptionHistory)
    if (!targetHistory.some((event) => event.id === `${operation.id}:in` && event.targetKeyBookingId === operation.keyId)) {
        await keyRequest('PUT', 'unit-keys', { id: operation.keyId, body: { ReceptionHistory: [...targetHistory.filter((event) => event.id !== `${operation.id}:in`), eventFor(operation)] } })
    }
    if (operation.source != null) {
        // Re-read immediately before closing; preserve newly appended reception events.
        sourceKey = await readKey(operation.source.keyId)
        const history = historyArray(sourceKey.attributes.ReceptionHistory)
        if (!history.some((event) => event.id === `${operation.id}:out`)) {
            if (sourceKey.attributes.Status !== 'In-process' || relationID(sourceKey.attributes.Holder) !== operation.source.holder.id) throw new Error('The original booking changed. Transfer is unfinished.')
            await keyRequest('PUT', 'unit-keys', { id: operation.source.keyId, body: { Status: 'Completed', DropOutDate: operation.pickup, ReceptionHistory: [...history, eventFor(operation, true)] } })
        }
        sourceKey = await readKey(operation.source.keyId)
        if (sourceKey.attributes.Status !== 'Completed' || !historyArray(sourceKey.attributes.ReceptionHistory).some((event) => event.id === `${operation.id}:out`)) throw new Error('Unable to confirm closure of the original key booking.')
    }
    targetKey = await readKey(operation.keyId!)
    verifyTarget(operation, await readViewing(operation.viewingId!), targetKey)
    if (!historyArray(targetKey.attributes.ReceptionHistory).some((event) => event.id === `${operation.id}:in` && event.targetKeyBookingId === operation.keyId)) throw new Error('Unable to confirm receiving key history.')
}
export async function runKeyOperation(operation: KeyOperation): Promise<void> {
    if (active.has(operation.id)) throw new Error('This operation is already being saved.')
    active.add(operation.id)
    try {
        await execute(operation)
    } finally { active.delete(operation.id) }
}
